/**
 * FaceEnrollment Component
 *
 * Guides user through face enrollment with liveness detection.
 * Generates keys client-side and sends only public data to server.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
  Progress,
  useColorModeValue,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@apollo/client';

import { FaceCapture } from './FaceCapture';
import { generateDeterministicFromFace } from '../lib/fuzzyExtractor';
import { deriveKeyPair } from '../lib/keyDerivation';
import { ZK_ENROLL, ZK_GET_SALT } from '../queries';
import type { FaceEnrollmentProps, ZKCredential } from '../types';

/* eslint-disable react-hooks/rules-of-hooks */

// ============================================================================
// Constants
// ============================================================================

// Number of face samples to capture during enrollment for maximum consistency
const ENROLLMENT_SAMPLE_COUNT = 5;

// ============================================================================
// Types
// ============================================================================

type EnrollmentStep = 'intro' | 'capture' | 'processing' | 'success' | 'error';

// ============================================================================
// Component
// ============================================================================

export const FaceEnrollment: React.FC<FaceEnrollmentProps> = ({
  onSuccess,
  onCancel,
}) => {
  // State
  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Hooks
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const infoBgColor = useColorModeValue('blue.50', 'blue.900');

  // GraphQL
  const [enrollMutation, { loading: enrolling }] = useMutation(ZK_ENROLL);
  const { data: saltData, loading: saltLoading, error: saltError } = useQuery(ZK_GET_SALT);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleStartEnrollment = useCallback(() => {
    setStep('capture');
    setError(null);
  }, []);

  const handleFaceCapture = useCallback(
    async (descriptor: Float32Array) => {
      console.log('[ZK Enroll] handleFaceCapture called with descriptor length:', descriptor.length);
      setStep('processing');
      setProgress(10);

      try {
        // Step 0: Check we have server salt
        if (!saltData?.zkGetSalt) {
          throw new Error('Server salt not available. Please try again.');
        }
        const serverSalt = saltData.zkGetSalt;
        console.log('[ZK Enroll] Server salt received:', serverSalt.substring(0, 16) + '...');

        // Step 1: Generate DETERMINISTIC key from face + server salt (client-side only)
        setProgress(30);
        console.log('[ZK Enroll] Step 1: Generating DETERMINISTIC key from face + salt...');
        console.log('[ZK Enroll] Descriptor sample (first 5 values):', Array.from(descriptor.slice(0, 5)));

        const { key: masterSecret, helperData } = generateDeterministicFromFace(descriptor, serverSalt);
        console.log('[ZK Enroll] Master secret generated (deterministic), length:', masterSecret.length);
        console.log('[ZK Enroll] Helper data length:', helperData.length);

        // Step 2: Derive key pair
        setProgress(50);
        console.log('[ZK Enroll] Step 2: Deriving key pair...');

        const keyPair = deriveKeyPair(masterSecret);
        console.log('[ZK Enroll] Key pair derived!');
        console.log('[ZK Enroll] Public key (hex):', keyPair.publicKeyHex.substring(0, 20) + '...');

        // Step 3: Send public data to server
        setProgress(70);
        console.log('[ZK Enroll] Step 3: Sending enrollment to server...');
        console.log('[ZK Enroll] Sending:', {
          publicKey: keyPair.publicKeyHex.substring(0, 20) + '...',
          helperDataLength: helperData.length,
          credentialType: 'FACE',
        });

        const { data } = await enrollMutation({
          variables: {
            input: {
              publicKey: keyPair.publicKeyHex,
              helperData: helperData,
              credentialType: 'FACE',
            },
          },
        });

        setProgress(100);
        console.log('[ZK Enroll] Server response:', data);

        if (data?.zkEnroll) {
          console.log('[ZK Enroll] Enrollment successful! ID:', data.zkEnroll.id);

          // Store the public key locally for future login
          // This is needed to fetch helper data during authentication
          try {
            const storedCredentials = JSON.parse(
              localStorage.getItem('zk_credentials') || '[]'
            );
            storedCredentials.push({
              publicKey: keyPair.publicKeyHex,
              credentialType: 'FACE',
              enrolledAt: new Date().toISOString(),
            });
            localStorage.setItem('zk_credentials', JSON.stringify(storedCredentials));
            console.log('[ZK Enroll] Public key stored locally for future authentication');
          } catch (storageErr) {
            console.warn('[ZK Enroll] Could not store public key locally:', storageErr);
          }

          setStep('success');

          toast({
            title: 'Enrollment Successful',
            description: 'Your face has been enrolled for authentication.',
            status: 'success',
            duration: 5000,
          });

          // Call success callback after brief delay
          setTimeout(() => {
            onSuccess(data.zkEnroll);
          }, 1500);
        }
      } catch (err) {
        console.error('[ZK Enroll] Enrollment failed:', err);
        console.error('[ZK Enroll] Error details:', {
          name: err instanceof Error ? err.name : 'unknown',
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        setError(err instanceof Error ? err.message : 'Enrollment failed');
        setStep('error');

        toast({
          title: 'Enrollment Failed',
          description: err instanceof Error ? err.message : 'Please try again.',
          status: 'error',
          duration: 5000,
        });
      }
    },
    [enrollMutation, onSuccess, toast, saltData]
  );

  const handleCaptureError = useCallback((errorMsg: string) => {
    setError(errorMsg);
  }, []);

  const handleRetry = useCallback(() => {
    setStep('capture');
    setError(null);
    setProgress(0);
  }, []);

  // ============================================================================
  // Render Steps
  // ============================================================================

  const renderIntro = () => (
    <VStack spacing={6} align="stretch" p={6}>
      <VStack spacing={2}>
        <Text fontSize="4xl">🔐</Text>
        <Heading size="md">Set Up Face Authentication</Heading>
        <Text color="gray.500" textAlign="center">
          Use your face to securely log in without passwords.
          Your biometric data never leaves your device.
        </Text>
      </VStack>

      <Box
        p={4}
        bg={infoBgColor}
        borderRadius="md"
      >
        <VStack spacing={2} align="start">
          <Text fontWeight="bold" fontSize="sm">How it works:</Text>
          <Text fontSize="sm">1. Position your face in the camera</Text>
          <Text fontSize="sm">2. Your device generates a unique key</Text>
          <Text fontSize="sm">3. Only a safe "helper" value is stored</Text>
          <Text fontSize="sm" color="green.500" fontWeight="medium">
            ✓ Private key never leaves your browser
          </Text>
        </VStack>
      </Box>

      <VStack spacing={3}>
        <Button
          colorScheme="blue"
          size="lg"
          width="full"
          onClick={handleStartEnrollment}
          isLoading={saltLoading}
          isDisabled={!!saltError}
        >
          {saltLoading ? 'Loading...' : 'Start Enrollment'}
        </Button>
        {saltError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              Failed to connect to server. Please check your connection.
            </AlertDescription>
          </Alert>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </VStack>
    </VStack>
  );

  const renderCapture = () => (
    <VStack spacing={4} p={4}>
      <Heading size="sm">Position Your Face</Heading>
      <Text fontSize="sm" color="gray.500" textAlign="center">
        We'll capture {ENROLLMENT_SAMPLE_COUNT} samples for best accuracy.
        Hold still when the countdown begins.
      </Text>

      <FaceCapture
        onCapture={handleFaceCapture}
        onError={handleCaptureError}
        autoCapture={true}
        showGuide={true}
        multiSampleCount={ENROLLMENT_SAMPLE_COUNT}
      />

      {error && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      <Button variant="ghost" onClick={onCancel} size="sm">
        Cancel
      </Button>
    </VStack>
  );

  const renderProcessing = () => (
    <VStack spacing={6} p={6} align="center">
      <Text fontSize="4xl">⚙️</Text>
      <Heading size="md">Securing Your Identity</Heading>
      <Text color="gray.500" textAlign="center">
        Generating cryptographic keys from your face...
      </Text>
      <Box width="full" px={4}>
        <Progress value={progress} colorScheme="blue" borderRadius="full" />
      </Box>
      <Text fontSize="sm" color="gray.400">
        {progress < 30 && 'Extracting face features...'}
        {progress >= 30 && progress < 50 && 'Generating secure key...'}
        {progress >= 50 && progress < 70 && 'Deriving key pair...'}
        {progress >= 70 && 'Saving to server...'}
      </Text>
    </VStack>
  );

  const renderSuccess = () => (
    <VStack spacing={6} p={6} align="center">
      <Text fontSize="5xl">✅</Text>
      <Heading size="md" color="green.500">
        Enrollment Complete!
      </Heading>
      <Text color="gray.500" textAlign="center">
        You can now log in using your face.
        Your private key is safe - it was never sent to our servers.
      </Text>
    </VStack>
  );

  const renderError = () => (
    <VStack spacing={6} p={6} align="center">
      <Text fontSize="5xl">❌</Text>
      <Heading size="md" color="red.500">
        Enrollment Failed
      </Heading>
      <Text color="gray.500" textAlign="center">
        {error || 'Something went wrong. Please try again.'}
      </Text>
      <HStack spacing={4}>
        <Button colorScheme="blue" onClick={handleRetry}>
          Try Again
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      maxW="400px"
      mx="auto"
    >
      {step === 'intro' && renderIntro()}
      {step === 'capture' && renderCapture()}
      {step === 'processing' && renderProcessing()}
      {step === 'success' && renderSuccess()}
      {step === 'error' && renderError()}
    </Box>
  );
};

export default FaceEnrollment;
